import React, { useState, useEffect, useRef } from 'react';
import { auth, db } from '../firebase';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { setDoc, doc, collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import './LoginRegisterModal.css';
import { useAuth } from '../context/AuthContext';

function LoginRegisterModal({ onClose }) {
    const [isLoginView, setIsLoginView] = useState(true);

    // Form fields
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');

    const [locationType, setLocationType] = useState('individual');
    const [location, setLocation] = useState('');
    const [locationId, setLocationId] = useState('');
    const [officeLocations, setOfficeLocations] = useState([]);
    const [studentLocations, setStudentLocations] = useState([]);
    const [isFetchingLocations, setIsFetchingLocations] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);
    
    const [studentIdFile, setStudentIdFile] = useState(null);
    const [studentIdBase64, setStudentIdBase64] = useState('');
    const [fileName, setFileName] = useState('');
    const fileInputRef = useRef(null);

    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchLocations = async () => {
            if (isLoginView || (locationType !== 'office' && locationType !== 'student')) {
                setOfficeLocations([]);
                setStudentLocations([]);
                return;
            }

            setIsFetchingLocations(true);
            setSelectedLocation(null); 
            
            try {
                const typeToQuery = locationType === 'office' ? 'office' : 'study_institute';
                
                const q = query(collection(db, 'locations'), where('locationType', '==', typeToQuery));
                const snapshot = await getDocs(q);
                const locationsData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

                if (locationType === 'office') {
                    setOfficeLocations(locationsData);
                } else {
                    setStudentLocations(locationsData);
                }
            } catch (e) { 
                console.error('Failed to fetch locations:', e); 
            } finally { 
                setIsFetchingLocations(false); 
            }
        };

        fetchLocations();
    }, [isLoginView, locationType]);

    const generateLocationId = () => `LOC_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`.toUpperCase();

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setStudentIdFile(file);
            setFileName(file.name);
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => setStudentIdBase64(reader.result);
            reader.onerror = (err) => {
                console.error("File reading error:", err);
                setError("Could not read the selected file.");
            };
        }
    };
    
    const handleRemoveFile = () => {
        setStudentIdFile(null);
        setFileName('');
        setStudentIdBase64('');
        if (fileInputRef.current) fileInputRef.current.value = null;
    };

    const handleLocationSelect = (e) => {
        const locationDocId = e.target.value;
        const allLocations = [...officeLocations, ...studentLocations];
        const loc = allLocations.find(l => l.id === locationDocId);

        if (loc) {
            setSelectedLocation(loc);
            setAddress(loc.address || '');
            setLocation(`${loc.latitude}, ${loc.longitude}`);
            setLocationId(loc.locationId || generateLocationId());
        } else {
            setSelectedLocation(null);
            setAddress('');
            setLocation('');
            setLocationId('');
        }
    };

    const handleGetLocation = () => {
        if (!navigator.geolocation) {
            alert('Geolocation is not supported.'); return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const { latitude, longitude } = pos.coords;
                setLocation(`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
                setLocationId(generateLocationId());
                alert('Location fetched! Please complete sign up.');
            },
            () => alert('Could not get location. Please grant permission.')
        );
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        if (locationType === 'student' && !studentIdFile) {
            setError('Please upload your Student ID card.'); return;
        }
        if ((locationType === 'office' || locationType === 'student') && !selectedLocation) {
            setError(`Please select an ${locationType === 'office' ? 'office' : 'institute'} from the list.`); return;
        }

        setLoading(true); setError('');
        try {
            const customerRef = collection(db, 'customer');
            // ✅ --- THIS IS THE CORRECTED QUERY ---
            const q = query(customerRef, orderBy('customerId', 'desc'), limit(1));
            const snapshot = await getDocs(q);
            let newId = 'C-0001';
            if (!snapshot.empty) {
                const lastId = snapshot.docs[0].data().customerId; // Use customerId here
                const num = parseInt(lastId.split('-')[1], 10) + 1;
                newId = `C-${num.toString().padStart(4, '0')}`;
            }

            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            const profileData = {
                customerId: newId, 
                name, email, phone, address,
                location: location || '',
                locationId: locationId || '',
                password: password,
                locationType: locationType,
                officeId: (locationType === 'office' || locationType === 'student') && selectedLocation ? selectedLocation.id : null,
                role: "User",
                studentIdVerified: locationType === 'student' ? "pending" : null,
                studentIdFile: locationType === 'student' ? {
                    name: studentIdFile.name, type: studentIdFile.type,
                    size: studentIdFile.size, base64: studentIdBase64,
                    uploadedAt: new Date().toISOString()
                } : null
            };

            if (profileData.studentIdVerified === null) delete profileData.studentIdVerified;
            if (profileData.studentIdFile === null) delete profileData.studentIdFile;
            
            await setDoc(doc(db, 'customer', user.uid), profileData, { merge: true });

            await signOut(auth);
            
            alert('Account created successfully! Please log in to continue.');
            setPassword('');
            setIsLoginView(true);

        } catch (err) {
            setError(err.message.replace('Firebase: ', ''));
            console.error('Registration Error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault(); setLoading(true); setError('');
        try {
            await signInWithEmailAndPassword(auth, email, password);
            alert('Logged in successfully!');
            onClose();
        } catch (err) {
            setError('Login failed. Please check your email and password.');
        } finally {
            setLoading(false);
        }
    };

    const ErrorDisplay = ({ message }) => (
        <div className="auth-error">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/></svg>
            <span>{message}</span>
        </div>
    );

    return (
        <div className="auth-popup-overlay" onClick={onClose}>
            <div className="auth-popup-container" onClick={(e) => e.stopPropagation()}>
                <button className="auth-popup-close-btn" onClick={onClose}>×</button>
                <div className="auth-popup-header">
                    <h2>{isLoginView ? 'Welcome Back!' : 'Create an Account'}</h2>
                </div>
                <div className="auth-popup-body">
                    <p className="subtitle">{isLoginView ? 'Log in to manage your account and subscriptions.' : 'Join us today! It takes less than a minute.'}</p>
                    {error && <ErrorDisplay message={error} />}
                    {isLoginView ? (
                        <form onSubmit={handleLogin} className="auth-popup-form">
                            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email *" required />
                            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password *" required />
                            <button type="submit" className="auth-popup-submit-btn" disabled={loading}>{loading ? 'LOGGING IN...' : 'LOG IN'}</button>
                        </form>
                    ) : (
                        <form onSubmit={handleRegister} className="auth-popup-form">
                            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full Name *" required />
                            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone *" required />
                            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email *" required />
                            
                            <div className="auth-row">
                                <select value={locationType} onChange={(e) => { setLocationType(e.target.value); setSelectedLocation(null); setAddress(''); setLocation(''); setLocationId(''); }}>
                                    <option value="student">Student</option>
                                    <option value="individual">Individual</option>
                                    <option value="office">Corporate</option>
                                </select>
                            </div>

                            {locationType === 'individual' && (
                                <>
                                    <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Delivery Address *" required />
                                    <div className="auth-row">
                                        <input type="text" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Coordinates (e.g., 11.02, 76.99)" />
                                        <button type="button" className="inline-btn" onClick={handleGetLocation}>Get Current Location</button>
                                    </div>
                                    <input type="text" value={locationId} onChange={(e) => setLocationId(e.target.value)} placeholder="Location ID (auto)" readOnly />
                                </>
                            )}

                            {locationType === 'office' && (
                                <>
                                    <div className="office-select-container">
                                        <select onChange={handleLocationSelect} required>
                                            <option value="">{isFetchingLocations ? 'Loading offices...' : 'Select an Office'}</option>
                                            {officeLocations.map(loc => (<option key={loc.id} value={loc.id}>{loc.companyName}</option>))}
                                        </select>
                                    </div>
                                    {selectedLocation && (<div className="office-card"><h4>{selectedLocation.companyName}</h4><p>{selectedLocation.address}</p></div>)}
                                    <input type="text" value={address} placeholder="Office Address" readOnly />
                                    <input type="text" value={location} placeholder="Office Coordinates" readOnly />
                                    <input type="text" value={locationId} placeholder="Location ID" readOnly />
                                </>
                            )}

                            {locationType === 'student' && (
                                <>
                                    <div className="office-select-container">
                                        <select onChange={handleLocationSelect} required>
                                            <option value="">{isFetchingLocations ? 'Loading institutes...' : 'Select an Institute'}</option>
                                            {studentLocations.map(loc => (<option key={loc.id} value={loc.id}>{loc.companyName}</option>))}
                                        </select>
                                    </div>
                                    {selectedLocation && (<div className="office-card"><h4>{selectedLocation.companyName}</h4><p>{selectedLocation.address}</p></div>)}
                                    
                                    <input type="text" value={address} placeholder="Institute Address" readOnly />
                                    <input type="text" value={location} placeholder="Institute Coordinates" readOnly />
                                    <input type="text" value={locationId} placeholder="Location ID" readOnly />

                                    <div className="file-input-wrapper">
                                        <label htmlFor="student-id-upload" className="file-input-label">{fileName ? 'Change ID' : 'Upload Student ID *'}</label>
                                        <input id="student-id-upload" type="file" onChange={handleFileChange} ref={fileInputRef} required accept="image/*,.pdf" />
                                        <span className="file-name-display">{fileName || "No file selected"}</span>
                                        {fileName && (<button type="button" onClick={handleRemoveFile} className="file-remove-btn" aria-label="Remove file">×</button>)}
                                    </div>
                                </>
                            )}

                            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Create Password *" required />
                            <button type="submit" className="auth-popup-submit-btn" disabled={loading}>{loading ? 'CREATING...' : 'CREATE ACCOUNT'}</button>
                        </form>
                    )}

                    <div className="auth-popup-toggle">
                        <span>{isLoginView ? "Don't have an account?" : 'Already have an account?'}</span>
                        <button onClick={() => setIsLoginView(!isLoginView)}>{isLoginView ? 'Sign Up' : 'Log In'}</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default LoginRegisterModal;